<template>
  <div class="video-player">
    <div class="video-player__player-wrapper">
      <div
        v-if="parentType !== 'course'"
        class="video-player__report-issue"
        @click.stop="startIssue"
      >
        <IssueIcon class="video-player__report-issue__icon" />
        <span class="video-player__report-issue__text">Report an issue</span>
      </div>
      <div id="player" ref="player"></div>
      <div
        v-show="isAdvanceContentButtonAvailable"
        class="video-player__advance"
        @click="advanceToNextMedia()"
      >
        <button class="video-player__advance-btn">
          {{ nextMediaText }} >>
        </button>
      </div>
    </div>
    <CreateBookmarkModal
      v-if="isCreatingBookmark"
      :pendingBookmark="pendingBookmark"
      @resume-video="instance.play()"
      @closed="isCreatingBookmark = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CreateBookmarkModal from '@/components/modals/CreateBookmarkModal.vue'
import CONSTANTS from '@/constants'
import IssueIcon from '@/assets/icons/course/bug.svg'
import { videoCommons } from '@/helpers/mixins'

export default {
  name: 'Video',
  components: {
    CreateBookmarkModal,
    IssueIcon: { ...IssueIcon, name: 'IssueIcon' },
  },
  mixins: [videoCommons],
  props: {
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    parent: {
      type: Object,
      required: false,
    },
    parentData: {
      type: Object,
      required: true,
    },
    content: {
      type: Object,
      required: true,
    },
    nextMedia: {
      type: String,
      required: false,
    },
    userCompletionStatus: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showCreateBookmarkModal: false,
      isAdvanceContentButtonShown: false,
      isCreatingBookmark: false,
      pendingBookmark: {},
    }
  },
  watch: {
    videoTime(time) {
      if (time !== 0) {
        this.instance.seek(time)
      }
    },
  },
  computed: {
    ...mapState({
      jwVideo: state => state.content.jwVideo,
      videoTime: state => state.content.playingVideoTime,
    }),
    isAdvanceContentButtonAvailable() {
      const { nextMediaText, isAdvanceContentButtonShown } = this
      return nextMediaText && isAdvanceContentButtonShown
    },
    hasWatched() {
      const { userCompletionStatus } = this
      return userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
    },
    nextMediaText() {
      switch (this.nextMedia) {
        case 'quiz':
          return 'Take Quiz'
        case 'video':
          return 'Play next video'
        default:
          return ''
      }
    },
  },
  async created() {
    this.$emit('is-loading', true)
    await this.$store.dispatch('content/getJWVideo', this.content.id)
    if (!this.hasWatched) {
      await this.$store.dispatch('content/updateVideoStatus', {
        videoId: this.content.id,
        status: CONSTANTS.CONTENT_USER_STATUS.STARTED,
      })
      if (this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE) {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_VIDEO_STARTED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          video_id: this.content.id,
          video_name: this.content.name,
          ...this.content,
        })
      }
    }

    const videoFetched = await this.$store.dispatch(
      'content/getVideoStatus',
      this.content.id
    )
    const { playlist } = this.jwVideo
    if (playlist) {
      this.setupPlayer(playlist)
      const isVideoCompleted =
        videoFetched?.current_position_in_secs ===
        this.content.duration_in_seconds

      if (isVideoCompleted) {
        this.$store.dispatch('content/updateVideoPosition', {
          videoId: this.content.id,
          position: 0,
        })
      }
      if (process.env.VUE_APP_RESUME_VIDEO_ENABLED) {
        const videoPosition = isVideoCompleted
          ? 0
          : videoFetched?.current_position_in_secs
        this.instance?.seek(videoPosition)
        if (videoPosition) this.isResumed = true
      }
    }
    this.$emit('is-loading', false)
    if (this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE) {
      this.sendMixpanelEvent(
        this.eventsConstants.CONTENT_VIDEO_MEDIA_REQUESTED,
        {
          course_id: this.parent.id,
          course_name: this.parent.name,
          video_id: this.content.id,
          video_name: this.content.name,
          trigger: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE,
          ...this.content,
        }
      )
    }
    if (this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.BOOTCAMP) {
      this.sendMixpanelEvent(
        this.eventsConstants.CONTENT_VIDEO_MEDIA_REQUESTED,
        {
          bootcamp_id: this.parent.id,
          bootcamp_name: this.parent.name,
          video_id: this.content.id,
          video_name: this.content.name,
          trigger: this.trigger,
          ...this.content,
        }
      )
    }
    if (this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST) {
      this.sendMixpanelEvent(
        this.eventsConstants.CONTENT_VIDEO_MEDIA_REQUESTED,
        {
          playlist_id: this.parent.id,
          playlist_name: this.parent.name,
          video_id: this.content.id,
          video_name: this.content.name,
          trigger: this.trigger,
          ...this.content,
        }
      )
    }
  },
  mounted() {
    this.$scrollTo('.video-player__player-wrapper', 1000, {
      container: 'body',
      offset: -220,
    })
  },
  destroyed() {
    this.$store.dispatch('content/getVideoStatus', this.content.id)
    document.querySelector('body').style.overflowY = 'visible'
    this.$store.dispatch('content/setPlayingVideoTime', 0)
    if (this.instance && this.instance.remove) this.instance.remove()
  },
  methods: {
    _addBookmarksButton() {
      this.instance.addButton(
        // This portion is what designates the graphic used for the button
        '/assets/icons/jwplayer/bookmark_icon.png',
        // This portion determines the text that appears as a tooltip
        'Bookmark Video',
        // This portion designates the functionality of the button itself
        () => {
          this._bookmarkVideo()
          if (this.instance.getFullscreen() === true) {
            this.instance.setFullscreen()
          }
          this.instance.pause()
        },
        // And finally, here we set the unique ID of the button itself.
        'bookmark'
      )
    },
    async _bookmarkVideo() {
      this.pendingBookmark = {
        time: parseInt(this.instance.getPosition()),
        videoId: this.content.id,
        title: this.content.name,
        courseId: this.parent.id,
      }
      this.isCreatingBookmark = true
    },
    advanceToNextMedia() {
      this.$emit('advance-media')
    },
    setupAdvanceContainer() {
      const advanceContainer = document.querySelector('.video-player__advance')
      const jwControls = document.querySelector('.jw-controlbar')
      if (jwControls) {
        jwControls.append(advanceContainer)
      }
    },

    async updateVideoPosition(isCompleted = false) {
      const position = parseInt(this.instance.getPosition())
      const roundedPosition = isCompleted ? this.content.duration_in_seconds : 0
      const currentPosition = roundedPosition || position
      await this.$store.dispatch('content/updateVideoPosition', {
        videoId: this.content.id,
        position: currentPosition,
      })
    },
    startIssue() {
      this.$store.dispatch('content/saveContentIssue', {
        content_type: 'video',
        content_subtype: this.content.type,
        content_name: this.content.name,
        content_id: this.content.id,
        parent_id: this.parent.id,
        parent_type: this.parentType,
        extra_args: {
          video_player_time: this.currentPosition,
          video_player_status: this.content.status,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.video-player {
  $this: &;
  position: relative;
  &__advance {
    position: absolute;
    bottom: 100%;
    right: 0;
    background: linear-gradient(
      to left bottom,
      rgb(82, 172, 94),
      rgb(135, 189, 140)
    );
    opacity: 0.8;
    width: 200px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top-left-radius: 25px;
    cursor: pointer;
    &:hover {
      opacity: 1;
      #{$this}__advance-btn {
        opacity: 1;
      }
    }
  }
  &__advance-btn {
    background: none;
    border: none;
    color: var(--white);
    font-size: 1rem;
    text-transform: uppercase;
    padding: 0 10% 0 0;
    opacity: 0.8;
    cursor: pointer;
  }
  &__report-issue {
    color: white;
    margin-left: auto;
    margin-right: 15px;
    display: flex;
    align-items: center;
    cursor: pointer;
    margin: 20px;
    text-align: right;
    justify-content: flex-end;
    &__icon {
      fill: #717171;
      margin-right: 5px;
    }
  }
  .jw-slider-time .jw-cue-type-bookmark {
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background: #fffec9;
  }
}
</style>
